/**
 * ContactForm - Unified contact create/edit form
 *
 * Supports two variants:
 * - dialog: Used in ContactSheet with external submit button
 * - inline: Used in EditFormContainer with built-in submit/cancel
 *
 * Data can be provided via:
 * - contact: ContactRow (snake_case database format)
 * - initialData: ContactFormInitialData (camelCase enriched format)
 */

import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { broadcastInvalidation } from '@/lib/sync'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import {
  contactFormSchema,
  type ContactFormInput,
  type ContactFormValues,
} from './contact-form-schema'
import {
  normalizeContactData,
  CONTACT_PREFOCUS_FIELD_MAP,
  type ContactFormInitialData,
} from './contact-form-utils'
import type { ContactRow } from '@/types/contacts'
import {
  CONTACT_STATUSES,
  CONTACT_STATUS_LABELS,
  CONTACT_METHODS,
  CONTACT_METHOD_LABELS,
} from '@/types/contacts'
import { updateContact } from '@/lib/api/contacts'

interface ContactFormProps {
  /** Existing contact data (snake_case database format) for edit mode */
  contact?: ContactRow | null
  /** Existing contact data (camelCase enriched format) for edit mode */
  initialData?: ContactFormInitialData | null
  /** Form variant - affects button placement */
  variant?: 'dialog' | 'inline'
  /** Field to auto-focus on mount (camelCase or snake_case) */
  prefocusField?: string
  /** Form ID for external submit button binding (dialog variant) */
  formId?: string
  /** Contact ID for built-in mutation (inline variant, edit mode) */
  contactId?: string
  /** Called when form is submitted with valid data (external handling) */
  onSubmit?: (data: ContactFormValues) => void | Promise<void>
  /** Called after successful mutation (inline variant) */
  onSuccess?: () => void
  /** Called when cancel button clicked */
  onCancel?: () => void
  /** External loading state (dialog variant) */
  isLoading?: boolean
}

export function ContactForm({
  contact,
  initialData,
  variant = 'dialog',
  prefocusField,
  formId = 'contact-form',
  contactId,
  onSubmit,
  onSuccess,
  onCancel,
  isLoading: externalLoading,
}: ContactFormProps) {
  const queryClient = useQueryClient()
  const isInline = variant === 'inline'
  const isEdit = !!(contact || initialData || contactId)

  // Initialize form with normalized data
  const form = useForm<ContactFormInput, unknown, ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: normalizeContactData(contact, initialData),
  })

  // Auto-focus the specified field
  useEffect(() => {
    if (prefocusField) {
      const fieldName = CONTACT_PREFOCUS_FIELD_MAP[prefocusField] || prefocusField
      if (fieldName && fieldName in form.getValues()) {
        setTimeout(() => form.setFocus(fieldName as keyof ContactFormInput), 100)
      }
    }
  }, [prefocusField, form])

  // Built-in mutation for inline edit mode
  const mutation = useMutation({
    mutationFn: async (values: ContactFormValues) => {
      if (!contactId) throw new Error('contactId required for mutation')
      return updateContact(contactId, {
        first_name: values.first_name,
        last_name: values.last_name,
        job_title: values.job_title || null,
        email_primary: values.email_primary,
        email_secondary: values.email_secondary || null,
        phone_office: values.phone_office || null,
        phone_mobile: values.phone_mobile || null,
        phone_other: values.phone_other || null,
        is_primary_contact: values.is_primary_contact,
        is_billing_contact: values.is_billing_contact,
        is_operations_contact: values.is_operations_contact,
        preferred_contact_method: values.preferred_contact_method,
        status: values.status,
        notes: values.notes || null,
      })
    },
    onSuccess: () => {
      toast.success('Contact updated')
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      queryClient.invalidateQueries({ queryKey: ['client-detail'] })
      queryClient.invalidateQueries({ queryKey: ['allocations'] })
      broadcastInvalidation([['contacts'], ['client-detail'], ['allocations']])
      onSuccess?.()
    },
    onError: (error) => {
      toast.error('Failed to update: ' + (error as Error).message)
    },
  })

  // Handle form submission
  const handleSubmit = async (data: ContactFormValues) => {
    if (onSubmit) {
      // External handling (dialog mode)
      await onSubmit(data)
    } else if (contactId) {
      // Built-in mutation (inline edit mode)
      mutation.mutate(data)
    }
  }

  const isSubmitting = externalLoading || mutation.isPending

  // Render role checkboxes (for dialog variant)
  const renderRoleCheckboxes = () => (
    <div className="flex flex-wrap gap-6">
      <FormField
        control={form.control}
        name="is_primary_contact"
        render={({ field }) => (
          <FormItem className="flex flex-row items-center space-x-2 space-y-0">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <FormLabel className="font-normal">Primary Contact</FormLabel>
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="is_billing_contact"
        render={({ field }) => (
          <FormItem className="flex flex-row items-center space-x-2 space-y-0">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <FormLabel className="font-normal">Billing Contact</FormLabel>
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="is_operations_contact"
        render={({ field }) => (
          <FormItem className="flex flex-row items-center space-x-2 space-y-0">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <FormLabel className="font-normal">Operations Contact</FormLabel>
          </FormItem>
        )}
      />
    </div>
  )

  // Render role switches (for inline variant)
  const renderRoleSwitches = () => (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">Primary Contact</span>
        <Switch
          checked={form.watch('is_primary_contact')}
          onCheckedChange={(checked) => form.setValue('is_primary_contact', checked)}
        />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">Billing Contact</span>
        <Switch
          checked={form.watch('is_billing_contact')}
          onCheckedChange={(checked) => form.setValue('is_billing_contact', checked)}
        />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">Operations Contact</span>
        <Switch
          checked={form.watch('is_operations_contact')}
          onCheckedChange={(checked) => form.setValue('is_operations_contact', checked)}
        />
      </div>
    </div>
  )

  return (
    <Form {...form}>
      <form
        id={isInline ? undefined : formId}
        onSubmit={form.handleSubmit(handleSubmit)}
        className="space-y-4"
      >
        {/* ── Contact Details ── */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium bg-muted/50 px-2 py-1.5 rounded-md">Contact Details</h3>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="first_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="John" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="last_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Smith" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="job_title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Job Title</FormLabel>
                  <FormControl>
                    <Input placeholder="Chief Pilot" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {CONTACT_STATUSES.map((status) => (
                        <SelectItem key={status} value={status}>
                          {CONTACT_STATUS_LABELS[status]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <Separator />

        {/* ── Contact Information ── */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium bg-muted/50 px-2 py-1.5 rounded-md">Contact Information</h3>
          <FormField
            control={form.control}
            name="email_primary"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email *</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="john@example.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email_secondary"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Secondary Email</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="john.personal@example.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="phone_office"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Office Phone</FormLabel>
                  <FormControl>
                    <Input placeholder="(602) 555-1234" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone_mobile"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Mobile Phone</FormLabel>
                  <FormControl>
                    <Input placeholder="(602) 555-5678" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone_other"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Other Phone</FormLabel>
                  <FormControl>
                    <Input placeholder="(602) 555-9012" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="preferred_contact_method"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Preferred Contact Method</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {CONTACT_METHODS.map((method) => (
                      <SelectItem key={method} value={method}>
                        {CONTACT_METHOD_LABELS[method]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Separator />

        {/* ── Contact Roles ── */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium bg-muted/50 px-2 py-1.5 rounded-md">Contact Roles</h3>
          {isInline ? renderRoleSwitches() : renderRoleCheckboxes()}
        </div>

        <Separator />

        {/* ── Notes ── */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium bg-muted/50 px-2 py-1.5 rounded-md">Notes</h3>
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    placeholder="Internal notes about this contact..."
                    className={isInline ? 'min-h-[60px] resize-none' : 'min-h-[80px]'}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Form actions - always shown for inline, dialog handles externally */}
        {isInline && (
          <div className="flex justify-end gap-2 pt-2 border-t">
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}

        {/* Dialog variant buttons - only when onCancel provided and not inline */}
        {!isInline && onCancel && (
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : isEdit ? 'Update Contact' : 'Create Contact'}
            </Button>
          </div>
        )}
      </form>
    </Form>
  )
}
